import { data, Link, redirect } from 'react-router';
import { ArrowLeft, Shapes } from 'lucide-react';
import type { Route } from './+types/thema';
import { PublicLayout } from '~/components/public/PublicLayout';
import { ArticleCardGrid } from '~/components/public/ArticleCard';
import { IconSvg } from '~/components/public/IconSvg';
import { seoMeta } from '~/lib/seo';

export function loader({ params, context }: Route.LoaderArgs) {
  const payload = context.services.themePayload(params.slug.toLowerCase());
  if (!payload) throw data({ message: 'Thema niet gevonden' }, { status: 404 });
  // Een alias of een gepensioneerd thema: 301 naar de huidige slug. Zo blijft
  // een bladwijzer van vóór een herclustering werken en verzamelt de nieuwe
  // URL de linkwaarde in plaats van dat die op twee adressen blijft hangen.
  if (payload.redirect) throw redirect(`/thema/${payload.redirect}`, 301);
  return payload;
}

export function meta({ data: d }: Route.MetaArgs) {
  return seoMeta(d?.seo);
}

export default function ThemaPage({ loaderData }: Route.ComponentProps) {
  const { theme, articles, alsoTouches } = loaderData;
  return (
    <PublicLayout>
      <header className="relative z-20 border-b border-hairline">
        <div className="ambient" aria-hidden />
        <div className="relative mx-auto max-w-[var(--container-app)] px-5 py-14">
          {/* Zelfde pil-taal als de hero op /, maar ZONDER stipje: dat pulseert
              en betekent "live/merk". Herhaald op een typelabel wordt het
              decoratie, en bij zes hero's ruis. */}
          <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-soft px-4 py-1.5 text-sm font-semibold tracking-wide text-accent-soft-ink">
            <Shapes size={13} aria-hidden /> Thema
          </p>
          <h1 className="flex items-center gap-3 font-display text-[clamp(1.75rem,1rem+2.6vw,2.75rem)] font-semibold text-ink">
            {theme.iconSvg && <IconSvg svg={theme.iconSvg} className="inline-flex text-accent" />}
            {theme.name}
          </h1>
          {theme.description && <p className="mt-2 max-w-2xl text-lg text-ink-2">{theme.description}</p>}
          <p className="mt-3 text-base text-ink-3 tabular-nums">
            {articles.length} {articles.length === 1 ? 'artikel' : 'artikelen'}
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[var(--container-app)] px-5 py-10">
        {articles.length > 0 ? (
          <ArticleCardGrid items={articles} />
        ) : (
          <div className="rounded-xl border border-hairline bg-surface px-8 py-14 text-center shadow-sm">
            <p className="text-lg text-ink-2">Nog geen artikelen in dit thema.</p>
            <p className="mt-1.5 text-base text-ink-3">
              Zoek in de hele kennisbank of kies een ander thema op de homepage.
            </p>
            <Link
              to={`/search?q=${encodeURIComponent(theme.name)}`}
              viewTransition
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-base font-semibold text-accent-on shadow-sm transition hover:bg-accent-hover hover:shadow-glow"
            >
              Zoeken op “{theme.name}”
            </Link>
          </div>
        )}

        {/* De enige publieke plek waar meervoudige toewijzing zichtbaar is.
            Bewust ónder de hoofdlijst en visueel rustiger: het zijn artikelen
            die dit thema raken, niet artikelen die erin thuishoren. */}
        {alsoTouches.length > 0 && (
          <section aria-labelledby="raakt-ook" className="mt-14 border-t border-hairline pt-10">
            <h2 id="raakt-ook" className="mb-1 font-display text-xl font-semibold text-ink">
              Raakt ook dit thema
            </h2>
            <p className="mb-5 text-base text-ink-3">
              Deze artikelen horen ergens anders thuis, maar gaan er deels ook over.
            </p>
            <ArticleCardGrid items={alsoTouches} />
          </section>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/"
            viewTransition
            className="inline-flex items-center gap-2 rounded-md border border-hairline-strong px-4 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
          >
            <ArrowLeft size={15} aria-hidden /> Terug naar de kennisbank
          </Link>
        </div>
      </div>
    </PublicLayout>
  );
}
